import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";


import ListingCard from "@/components/listing/ListingCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { NativeSelect } from "@/components/ui/native-select";
import { Badge } from "@/components/ui/badge";
import { SIZE_PRESETS, COLOR_PRESETS } from "@/data/variantPresets";
import { Sparkles, Loader2, SlidersHorizontal, Map as MapIcon, Navigation, X, Bell, Plane } from "lucide-react";
import { toast } from "sonner";
import { useUserLocation, listingDistanceFrom, formatDistance } from "@/hooks/useUserLocation";
import { useCurrency } from "@/context/CurrencyContext";
import SEO from "@/components/SEO";

const Browse = () => {
  const [params, setParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [meta, setMeta] = useState({ countries: [], product_categories: [], service_categories: [], regions: {} });
  const [saving, setSaving] = useState(false);
  const { coords: userCoords, state: locState, request: requestLoc, clear: clearLoc } = useUserLocation();
  const { userCountry } = useCurrency();

  const type = params.get("type") || "";
  const category = params.get("category") || "";
  const country = params.get("country") || "";
  const region = params.get("region") || "";
  const q = params.get("q") || "";
  const sort = params.get("sort") || "featured"; // featured | newest | nearest
  const size = params.get("size") || "";
  const color = params.get("color") || "";
  const shipsToMe = params.get("ships_to_me") === "1";

  // Flatten preset sizes for the chosen category (or a sensible default mix)
  const sizeOptions = React.useMemo(() => {
    const groups = SIZE_PRESETS[category];
    if (groups) {
      const out = [];
      for (const [g, opts] of Object.entries(groups)) {
        out.push({ group: g, opts });
      }
      return out;
    }
    // Default mix when no category is selected (or category has no presets)
    return [
      { group: "Clothing", opts: ["XS", "S", "M", "L", "XL", "XXL"] },
      { group: "Shoes (UK)", opts: ["UK 5", "UK 6", "UK 7", "UK 8", "UK 9", "UK 10", "UK 11"] },
    ];
  }, [category]);

  useEffect(() => { api.get("/meta").then((r) => setMeta(r.data)); }, []);

  useEffect(() => {
    setLoading(true);
    // "Ships to me" toggle sends the buyer's detected country to the backend
    // so it can mix local listings + products that ship internationally.
    const shipsParam = shipsToMe && userCountry ? userCountry : undefined;
    api.get("/listings", {
      params: { type: type || undefined, category: category || undefined, country: country || undefined, q: q || undefined, size: size || undefined, color: color || undefined, ships_to_country: shipsParam },
    })
      .then((r) => {
        // Client-side region filter (region stored on the listing as city).
        let data = r.data;
        if (region) data = data.filter((l) => (l.city || "").toLowerCase() === region.toLowerCase());
        setListings(data);
      })
      .finally(() => setLoading(false));
  }, [type, category, country, q, region, size, color, shipsToMe, userCountry]);

  const updateParam = (key, value) => {
    const p = new URLSearchParams(params);
    if (value && value !== "all") p.set(key, value); else p.delete(key);
    setParams(p);
  };

  // Update multiple params atomically — needed when one selection clears another
  // (e.g. picking a new country also resets the region). Calling `updateParam`
  // twice in a row used a stale `params` closure for the second call and
  // clobbered the first update.
  const updateParams = (updates) => {
    const p = new URLSearchParams(params);
    for (const [key, value] of Object.entries(updates)) {
      if (value && value !== "all") p.set(key, value); else p.delete(key);
    }
    setParams(p);
  };

  const aiSearch = async () => {
    if (!q.trim()) {
      toast.error("Type a search query first");
      return;
    }
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/search", { query: q });
      setListings(data.results || []);
      const f = data.filters || {};
      const summary = [f.type, f.category, f.country].filter(Boolean).join(" · ");
      toast.success(summary ? `AI parsed: ${summary}` : "Showing best matches");
    } catch (e) {
      toast.error("AI search failed");
    } finally { setAiLoading(false); }
  };

  const saveSearch = async () => {
    setSaving(true);
    try {
      const payload = { type: type || null, category: category || null, country: country || null, region: region || null, q: q || null };
      const { data } = await api.post("/saved-searches", payload);
      toast.success(`Saved "${data.name}" — we'll email you new matches`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Please log in to save searches");
    } finally { setSaving(false); }
  };

  const cats = Array.from(new Set(
    type === "service" ? (meta.service_categories || [])
    : type === "product" ? (meta.product_categories || [])
    : [...(meta.product_categories || []), ...(meta.service_categories || [])]
  ));

  // Apply sort selection (server returns Pro-first + newest by default).
  const sortedListings = React.useMemo(() => {
    if (sort === "newest") {
      return [...listings].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    }
    if (sort === "nearest" && userCoords) {
      return [...listings].sort((a, b) => {
        const da = listingDistanceFrom(a, userCoords);
        const db = listingDistanceFrom(b, userCoords);
        if (da == null && db == null) return 0;
        if (da == null) return 1;
        if (db == null) return -1;
        return da - db;
      });
    }
    return listings; // featured (Pro-first) — already sorted server-side
  }, [listings, sort, userCoords]);

  // Build dynamic SEO title from filters
  const seoFilters = [type === "service" ? "Services" : type === "product" ? "Products" : null, category, region || country].filter(Boolean);
  const seoTitle = seoFilters.length ? `${seoFilters.join(" · ")} on AfriMarket` : "Browse the African diaspora marketplace";
  const seoDesc = `Discover ${seoFilters.length ? seoFilters.join(", ").toLowerCase() : "African products and trusted local services"} on AfriMarket. Authentic, secure, with our buyer guarantee.`;

  return (
    <div className="heritage-canvas min-h-screen">
      <div className="container-narrow py-10 relative">
      <SEO
        title={seoTitle}
        description={seoDesc}
        path={`/browse${params.toString() ? "?" + params.toString() : ""}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": seoTitle,
          "description": seoDesc,
          "url": `https://afrimarket.org/browse`,
        }}
      />
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <div className="label-overline text-[#C05A3C] mb-2">Browse</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight">
            {type === "service" ? "Local services" : type === "product" ? "Products from Africa" : "All listings"}
          </h1>
          <p className="text-muted-foreground mt-2 font-sans-ui">
            {sortedListings.length} result{sortedListings.length === 1 ? "" : "s"}{q ? ` for "${q}"` : ""}
          </p>
          <div className="mt-3 inline-flex items-center gap-2 text-xs">
            <span className="label-overline text-muted-foreground">Sort by</span>
            <NativeSelect data-testid="filter-sort" value={sort} onValueChange={(v) => updateParam("sort", v === "featured" ? null : v)} className="w-44">
              <option value="featured">Featured · Pro first</option>
              <option value="newest">Newest</option>
              <option value="nearest" disabled={!userCoords}>{`Nearest${!userCoords ? " (enable distance)" : ""}`}</option>
            </NativeSelect>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {userCountry && (
            <Button
              data-testid="ships-to-me-toggle"
              variant={shipsToMe ? "default" : "outline"}
              onClick={() => updateParam("ships_to_me", shipsToMe ? null : "1")}
              className={`rounded-md ${shipsToMe ? "bg-[#C05A3C] hover:bg-[#A8492F] text-white" : "border-[#C05A3C] text-[#C05A3C] hover:bg-[#C05A3C] hover:text-white"}`}
            >
              <Plane className="w-4 h-4 mr-2"/>
              {shipsToMe ? `Showing items that ship to ${userCountry}` : `Ships to ${userCountry}`}
            </Button>
          )}
          {userCoords ? (
            <Button data-testid="distance-toggle-off" variant="outline" onClick={() => { clearLoc(); toast.success("Distance hidden"); }} className="rounded-md">
              <X className="w-4 h-4 mr-2"/> Hide distance
            </Button>
          ) : (
            <Button data-testid="distance-toggle-on" variant="outline" onClick={() => { requestLoc(); }} disabled={locState === "requesting"} className="rounded-md">
              {locState === "requesting" ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Navigation className="w-4 h-4 mr-2"/>}
              Show distance
            </Button>
          )}
          <Link to={`/map${params.toString() ? `?${params.toString()}` : ""}`} data-testid="browse-map-btn">
            <Button variant="outline" className="rounded-md"><MapIcon className="w-4 h-4 mr-2"/> Map view</Button>
          </Link>
          <Button data-testid="save-search-btn" onClick={saveSearch} disabled={saving} variant="outline" className="rounded-md">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Bell className="w-4 h-4 mr-2"/>}
            Save search
          </Button>
          <Button data-testid="ai-search-btn" onClick={aiSearch} disabled={aiLoading} variant="outline" className="border-[#C05A3C] text-[#C05A3C] hover:bg-[#C05A3C] hover:text-white rounded-md">
            {aiLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Sparkles className="w-4 h-4 mr-2"/>}
            AI Smart Search
          </Button>
        </div>
      </div>

      <div className="bg-white border border-black/10 rounded-lg p-5 mb-8 grid sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="browse-filters">
        <div className="lg:col-span-2">
          <label className="label-overline text-muted-foreground block mb-2">Keyword</label>
          <Input
            data-testid="filter-q"
            value={q}
            onChange={(e) => updateParam("q", e.target.value)}
            placeholder="Search title or description"
            className="bg-white"
          />
        </div>
        <div>
          <label className="label-overline text-muted-foreground block mb-2">Type</label>
          <NativeSelect data-testid="filter-type" value={type || "all"} onValueChange={(v) => updateParam("type", v)}>
            <option value="all">All</option>
            <option value="product">Products</option>
            <option value="service">Services</option>
          </NativeSelect>
        </div>
        <div>
          <label className="label-overline text-muted-foreground block mb-2">Category</label>
          <NativeSelect data-testid="filter-category" value={category || "all"} onValueChange={(v) => updateParam("category", v)}>
            <option value="all">Any</option>
            {cats.map((c) => <option key={c} value={c}>{c}</option>)}
          </NativeSelect>
        </div>
        <div>
          <label className="label-overline text-muted-foreground block mb-2">Country</label>
          <NativeSelect data-testid="filter-country" value={country || "all"} onValueChange={(v) => updateParams({ country: v, region: "all" })}>
            <option value="all">Any</option>
            {meta.countries?.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
          </NativeSelect>
        </div>
      </div>

      {country && meta.regions?.[country]?.length > 0 && (
        <div className="bg-white border border-black/10 rounded-lg p-5 mb-8" data-testid="region-filter-row">
          <label className="label-overline text-muted-foreground block mb-2">Area in {country}</label>
          <NativeSelect data-testid="filter-region" value={region || "all"} onValueChange={(v) => updateParam("region", v)} className="max-w-md">
            <option value="all">Any area</option>
            {meta.regions[country].map((r) => <option key={r} value={r}>{r}</option>)}
          </NativeSelect>
        </div>
      )}

      {/* Variant filters — only meaningful for products, hidden for services */}
      {type !== "service" && (
        <div className="bg-white border border-black/10 rounded-lg p-5 mb-8" data-testid="variant-filter-row">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="label-overline text-muted-foreground block mb-2">My size</label>
              <NativeSelect data-testid="filter-size" value={size || "all"} onValueChange={(v) => updateParam("size", v)}>
                <option value="all">Any size</option>
                {sizeOptions.map(({ group, opts }) => (
                  <optgroup key={group} label={group}>
                    {opts.map((o) => <option key={o} value={o}>{o}</option>)}
                  </optgroup>
                ))}
              </NativeSelect>
            </div>
            <div>
              <label className="label-overline text-muted-foreground block mb-2">Colour</label>
              <NativeSelect data-testid="filter-color" value={color || "all"} onValueChange={(v) => updateParam("color", v)}>
                <option value="all">Any colour</option>
                {COLOR_PRESETS.map((c) => <option key={c} value={c}>{c}</option>)}
              </NativeSelect>
            </div>
          </div>
          {(size || color) && (
            <div className="text-xs text-muted-foreground mt-2 font-sans-ui">
              Showing only listings that explicitly tagged {size && <strong>&ldquo;{size}&rdquo;</strong>}{size && color && " · "}{color && <strong>&ldquo;{color}&rdquo;</strong>}. Some sellers haven&apos;t tagged variants yet.
            </div>
          )}
        </div>
      )}

      {(type || category || country || region || q || size || color || shipsToMe) && (
        <div className="flex flex-wrap gap-2 mb-6">
          {type && <Badge className="bg-[#F3EFE9] text-[#1A1A1A]">{type}</Badge>}
          {category && <Badge className="bg-[#F3EFE9] text-[#1A1A1A]">{category}</Badge>}
          {country && <Badge className="bg-[#F3EFE9] text-[#1A1A1A]">{country}</Badge>}
          {region && <Badge className="bg-[#F3EFE9] text-[#1A1A1A]">{region}</Badge>}
          {size && <Badge className="bg-[#1A2024] text-white">Size {size}</Badge>}
          {color && <Badge className="bg-[#C05A3C] text-white">{color}</Badge>}
          {shipsToMe && userCountry && <Badge className="bg-[#C05A3C] text-white" data-testid="ships-to-me-chip"><Plane className="w-3 h-3 mr-1 inline"/> Ships to {userCountry}</Badge>}
          {q && <Badge className="bg-[#F3EFE9] text-[#1A1A1A]">&ldquo;{q}&rdquo;</Badge>}
          <button onClick={() => setParams({})} className="text-xs text-muted-foreground hover:text-[#C05A3C] underline" data-testid="clear-filters">Clear all</button>
        </div>
      )}

      {loading ? (
        <div className="py-24 text-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin inline mr-2"/> Loading listings…</div>
      ) : listings.length === 0 ? (
        <div className="py-24 text-center">
          <SlidersHorizontal className="w-10 h-10 mx-auto text-muted-foreground mb-3"/>
          <h3 className="font-display text-2xl">No listings match those filters</h3>
          <p className="text-muted-foreground mt-2">Try clearing some filters or use AI Smart Search.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="browse-grid">
          {sortedListings.map((l) => (
            <ListingCard
              key={l.listing_id}
              listing={l}
              distanceLabel={userCoords ? formatDistance(listingDistanceFrom(l, userCoords)) : null}
            />
          ))}
        </div>
      )}
      </div>
    </div>
  );
};

export default Browse;
